import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2, Compass } from "lucide-react";

const rawApiUrl = process.env.REACT_APP_API_URL || "https://ensileta-erp-production.up.railway.app";
const API_URL = rawApiUrl.replace(/\/$/, "");

function LoginPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: ""
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));
    if (token && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      alert("Please fill all fields");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: form.email,
          password: form.password
        })
      });

      const result = await res.json();
      if (!res.ok) {
        alert(result.message || "Login Failed");
        setLoading(false);
        return;
      }

      if (result.success) {
        localStorage.setItem("token", result.token);
        localStorage.setItem("user", JSON.stringify(result.user));
        navigate("/dashboard", { replace: true });
      } else {
        alert(result.message || "Invalid Login");
      }
    } catch (err) {
      console.log(err);
      alert("Unable to connect to server");
    } finally {
      setLoading(false);
    }
  };

  // Modern UI Style Guide
  const s = {
    brandDark: "#0f172a",
    accentBlue: "#2563eb",
    borderActive: "#3b82f6",
    borderIdle: "#e2e8f0",
    textMuted: "#64748b",
    bgLight: "#f8fafc"
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", width: "100vw", background: s.bgLight, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', overflow: "hidden" }}>
      
      {/* LEFT PANEL: LUXURY ARCHITECTURAL HERO BANNER */}
      <div style={{
        flex: "1.2",
        position: "relative",
        background: `linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.85)), url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=1200')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "between",
        padding: "60px",
        color: "#ffffff",
        boxSizing: "border-box"
      }}>
        {/* Top Watermark */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", opacity: 0.9 }}>
          <div style={{ background: "rgba(255,255,255,0.1)", padding: "8px", borderRadius: "8px", backdropFilter: "blur(4px)" }}>
            <Compass size={22} color="#3b82f6" />
          </div>
          <span style={{ fontSize: "14px", fontWeight: "600", letterSpacing: "0.1em" }}>ENTERPRISE PLATFORM</span>
        </div>

        {/* Center Typography Block */}
        <div style={{ marginTop: "auto", marginBottom: "auto", maxWidth: "540px" }}>
          <h1 style={{ fontSize: "56px", fontWeight: "800", letterSpacing: "-0.03em", lineHeight: "1.05", marginBottom: "16px", background: "linear-gradient(to right, #ffffff, #cbd5e1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            EIMS
          </h1>
          <div style={{ width: "40px", height: "4px", background: s.accentBlue, borderRadius: "2px", marginBottom: "20px" }}></div>
          <p style={{ fontSize: "20px", fontWeight: "500", color: "#94a3b8", lineHeight: "1.4", margin: 0 }}>
            Ensileta Interiors Modular Solutions
          </p>
          <p style={{ fontSize: "14px", color: "#64748b", marginTop: "12px", lineHeight: "1.5" }}>
            Streamlining high-end commercial, corporate office, and industrial interior space engineering environments.
          </p>
        </div>

        {/* Footer Meta */}
        <div style={{ fontSize: "12px", color: "#475569", fontWeight: "500", marginTop: "auto" }}>
          SYSTEM PRODUCTION v2.4.0
        </div>
      </div>

      {/* RIGHT PANEL: MINIMAL INTERACTIVE INTERFACE CARD */}
      <div style={{
        flex: "1",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px",
        background: "#ffffff",
        boxSizing: "border-box",
        borderLeft: "1px solid #f1f5f9"
      }}>
        <div style={{ width: "100%", maxWidth: "380px" }}>
          
          {/* Header Block */}
          <div style={{ marginBottom: "36px" }}>
            <h2 style={{ fontSize: "28px", fontWeight: "700", color: s.brandDark, letterSpacing: "-0.02em", marginBottom: "6px" }}>
              Welcome back
            </h2>
            <p style={{ fontSize: "14px", color: s.textMuted, margin: 0 }}>
              Please enter your credentials to access your dashboard.
            </p>
          </div>

          <form onSubmit={handleLogin}>
            
            {/* EMAIL CONTAINER */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                Corporate Email Address
              </label>
              <div style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                border: `1px solid ${focusedField === "email" ? s.borderActive : s.borderIdle}`,
                borderRadius: "10px",
                transition: "all 0.2s ease",
                boxShadow: focusedField === "email" ? "0 0 0 3px rgba(59, 130, 246, 0.12)" : "none"
              }}>
                <Mail size={16} style={{ position: "absolute", left: "14px", color: focusedField === "email" ? s.accentBlue : s.textMuted }} />
                <input
                  type="email"
                  autoComplete="off"
                  name="email"
                  placeholder="name@company.com"
                  value={form.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField("")}
                  style={{
                    width: "100%",
                    height: "46px",
                    padding: "0 14px 0 42px",
                    border: "none",
                    outline: "none",
                    borderRadius: "10px",
                    fontSize: "14px",
                    color: s.brandDark,
                    background: "transparent",
                    boxSizing: "border-box"
                  }}
                />
              </div>
            </div>

            {/* PASSWORD CONTAINER */}
            <div style={{ marginBottom: "28px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                  Security Password
                </label>
              </div>
              <div style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                border: `1px solid ${focusedField === "password" ? s.borderActive : s.borderIdle}`,
                borderRadius: "10px",
                transition: "all 0.2s ease",
                boxShadow: focusedField === "password" ? "0 0 0 3px rgba(59, 130, 246, 0.12)" : "none"
              }}>
                <Lock size={16} style={{ position: "absolute", left: "14px", color: focusedField === "password" ? s.accentBlue : s.textMuted }} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField("")}
                  style={{
                    width: "100%",
                    height: "46px",
                    padding: "0 46px 0 42px",
                    border: "none",
                    outline: "none",
                    borderRadius: "10px",
                    fontSize: "14px",
                    color: s.brandDark,
                    background: "transparent",
                    boxSizing: "border-box"
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "4px",
                    height: "38px",
                    width: "38px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: s.textMuted,
                    borderRadius: "8px"
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* SUBMIT EXECUTION TRIGGER */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                height: "46px",
                border: "none",
                borderRadius: "10px",
                background: loading ? "#94a3b8" : s.brandDark,
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: "600",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)"
              }}
              onMouseEnter={(e) => { if(!loading) e.currentTarget.style.backgroundColor = "#1e293b"; }}
              onMouseLeave={(e) => { if(!loading) e.currentTarget.style.backgroundColor = s.brandDark; }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                  Verification in progress...
                </>
              ) : (
                "Authenticate Account"
              )}
            </button>
          </form>

          {/* SYSTEM COPYRIGHT LABELS */}
          <div style={{ marginTop: "40px", textAlign: "center", color: s.textMuted, fontSize: "12px", fontWeight: "500" }}>
            © 2026 EIMS ERP System
          </div>
        </div>
      </div>

      {/* Global CSS Injecting for standard loader tracking spins */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default LoginPage;